import React from 'react';
import Layout from '../components/Layout';

const Refund: React.FC = () => {
  return (
    <Layout title="Cancellation & Refund Policy" subtitle="Effective Date: 28 August 2024">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Introduction */}
        <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
          <p className="text-white/90 leading-relaxed">
            At Incupie Technologies Private Limited, we strive to provide excellent service and
            customer satisfaction. This Cancellation & Refund Policy outlines the terms and
            conditions for cancellations and refunds of our AI and digital compression services.
          </p>
        </div>

        {/* Policy Sections */}
        <div className="space-y-6">
          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">1. Subscription Cancellations</h2>
            <p className="text-white/90 mb-4">
              You may cancel your subscription at any time through your account dashboard or by
              contacting our support team. Upon cancellation:
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4">
              <li>Your subscription will remain active until the end of the current billing period</li>
              <li>You will continue to have access to all features until the subscription expires</li>
              <li>No further charges will be made to your payment method</li>
              <li>You will receive a confirmation email of the cancellation</li>
            </ul>
          </div>

          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">2. Refund Eligibility</h2>
            <p className="text-white/90 mb-4">Refunds may be provided under the following circumstances:</p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4">
              <li><strong className="text-white">Technical Issues:</strong> If our service experiences significant downtime or technical problems that prevent you from using the service</li>
              <li><strong className="text-white">Billing Errors:</strong> If you were charged incorrectly due to system errors</li>
              <li><strong className="text-white">Unused Service:</strong> If you cancel within 7 days of initial subscription and have not used the service</li>
              <li><strong className="text-white">Service Discontinuation:</strong> If we discontinue a service you've paid for without providing adequate notice</li>
            </ul>
          </div>

          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">3. Refund Process</h2>
            <p className="text-white/90 mb-4">To request a refund:</p>
            <ol className="list-decimal list-inside text-white/80 space-y-2 ml-4 mb-4">
              <li>Contact our support team at <a href="mailto:support@incupie.com" className="text-primary-400 hover:text-primary-300 transition-colors">support@incupie.com</a></li>
              <li>Provide your account details and reason for the refund request</li>
              <li>Include any relevant documentation or screenshots if applicable</li>
              <li>Wait for our team to review your request (usually within 2-3 business days)</li>
            </ol>
            <p className="text-white/90">
              Approved refunds will be processed within 5-10 business days and will be credited
              back to the original payment method.
            </p>
          </div>

          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">4. Non-Refundable Items</h2>
            <p className="text-white/90 mb-4">The following are generally non-refundable:</p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4">
              <li>One-time processing fees or setup costs</li>
              <li>Services that have been fully utilized or consumed</li>
              <li>Subscriptions cancelled after 30 days of the billing date</li>
              <li>Custom development or consulting services</li>
              <li>Third-party service fees or charges</li>
            </ul>
          </div>

          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">5. Prorated Refunds</h2>
            <p className="text-white/90">
              In certain cases, we may provide prorated refunds based on the unused portion of
              your subscription. This is evaluated on a case-by-case basis and is at the sole
              discretion of Incupie Technologies Private Limited.
            </p>
          </div>

          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">6. Payment Disputes</h2>
            <p className="text-white/90">
              If you dispute a charge with your bank or credit card company before contacting us,
              we may suspend your account until the dispute is resolved. We encourage you to
              contact our support team first to resolve any billing issues.
            </p>
          </div>

          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">7. Force Majeure</h2>
            <p className="text-white/90">
              Incupie shall not be liable for any delays or failures in service due to circumstances
              beyond our reasonable control, including but not limited to natural disasters,
              government actions, or technical failures of third-party services.
            </p>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gradient-to-r from-primary-500/10 to-primary-600/10 border border-primary-500/20 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white mb-4">Need Help?</h2>
          <p className="text-white/90 mb-4">
            If you have questions about our refund policy or need assistance with a cancellation:
          </p>
          <div className="space-y-2 text-white/90">
            <p><strong className="text-white">Email:</strong>{' '}
              <a href="mailto:support@incupie.com" className="text-primary-400 hover:text-primary-300 transition-colors">
                support@incupie.com
              </a>
            </p>
            <p><strong className="text-white">Phone:</strong> +91-9915080818</p>
            <p><strong className="text-white">Support Hours:</strong> Monday - Friday, 9:00 AM to 6:00 PM IST</p>
            <p><strong className="text-white">Address:</strong><br />
               Incupie Technologies Private Limited<br />
               No. 19, 4th C Cross Koramangala Industrial,<br />
               5th Block, Area, Bengaluru, Karnataka 560095, India</p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Refund;
